import type { ChatMessage } from '@/modules/chat/types'
import { findConfirmationOutcome } from './confirmationOutcome'

export const REPORT_EDIT_APPLY_TOOL = {
  applyToolName: 'apply_report_edit',
  tokenField: 'token',
}

interface StagedReportEdit {
  token: string
  expiresAt: string
}

interface ReportEditPreviewPayload {
  kind?: string
  spec?: { token?: string; expires_at?: string }
}

// Cards staged by one assistant message all send their results back in the
// next user message, so that message is the card's group for Apply all.
export function pendingSiblingReportEditTokens(
  messages: ChatMessage[],
  token: string,
  cancelledTokens: string[],
  now: number = Date.now(),
): string[] {
  return stagedGroupContaining(messages, token)
    .filter((staged) => isStillPending(messages, staged, cancelledTokens, now))
    .map((staged) => staged.token)
}

function stagedGroupContaining(
  messages: ChatMessage[],
  token: string,
): StagedReportEdit[] {
  for (const message of messages) {
    const group = stagedReportEdits(message)
    if (group.some((staged) => staged.token === token)) return group
  }
  return []
}

function stagedReportEdits(message: ChatMessage): StagedReportEdit[] {
  if (message.role !== 'user') return []
  return message.contentJson.flatMap((block) => {
    if (block.type !== 'tool_result' || block.is_error) return []
    const staged = parseStagedReportEdit(block.content)
    return staged ? [staged] : []
  })
}

function parseStagedReportEdit(content: string): StagedReportEdit | null {
  let payload: ReportEditPreviewPayload
  try {
    payload = JSON.parse(content) as ReportEditPreviewPayload
  } catch {
    return null
  }
  if (payload.kind !== 'report_edit_preview' || !payload.spec?.token) {
    return null
  }
  return { token: payload.spec.token, expiresAt: payload.spec.expires_at ?? '' }
}

function isStillPending(
  messages: ChatMessage[],
  staged: StagedReportEdit,
  cancelledTokens: string[],
  now: number,
): boolean {
  if (cancelledTokens.includes(staged.token)) return false
  if (!(Date.parse(staged.expiresAt) > now)) return false
  const outcome = findConfirmationOutcome(messages, {
    ...REPORT_EDIT_APPLY_TOOL,
    token: staged.token,
  })
  return outcome === null
}
